import {
  RevButton,
  RevChip,
  RevPopoverMenu,
  RevTextArea,
} from "@revmatics/catalyst";
import { useEffect, useMemo, useState } from "react";
import { faEye, faEyeSlash, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { ReasonForHideType } from "../../../../../../../../stores/types/creatorsTypes.tsx";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../../stores/hooks/hooks.ts";
import { CloseButton } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  deleteStatusCreator,
  interactionsCreator,
} from "../../../../../../../../stores/thunks/creatorsThunk.tsx";

type Props = {
  initHidden: boolean;
  creatorId: number;
  id: string;
  width?: string;
};
export const HideCreatorMenu = ({
  initHidden,
  creatorId,
  id,
  width,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otherReasonId, setOtherReasonId] = useState<number | null>(null);
  const [hidden, setHidden] = useState(initHidden);
  const dispatch = useAppDispatch();
  const [selectedReasons, setSelectedReasons] = useState<Set<number>>(
    new Set(),
  );
  const [otherReason, setOtherReason] = useState("");

  const reasonsForHide = useAppSelector(
    (state) => state.creators.reasonsForHide,
  );
  const isLoadingReasons = useAppSelector(
    (state) => state.creators.isLoadingReasons,
  );
  const [localHidden, setLocalHidden] = useState(hidden);

  useEffect(() => {
    if ((otherReason || selectedReasons.size > 0) && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isSubmitting, otherReason, selectedReasons]);

  const handleHidden = () => {
    if (creatorId) {
      if (hidden) {
        dispatch(deleteStatusCreator(creatorId));
      } else {
        dispatch(
          interactionsCreator({
            id: creatorId,
            body: {
              status: "hidden",
              hide_reason_note: otherReason,
              hide_reasons: Array.from(selectedReasons),
            },
          }),
        ).then(() => {
          setHidden(true);
        });
      }
    }
  };

  const setReason = (value: number) => {
    const newSet = new Set(selectedReasons);
    if (selectedReasons.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }
    setSelectedReasons(newSet);
  };

  const reasonsForHideList = useMemo(() => {
    if (!reasonsForHide) return [];
    const withoutOther = reasonsForHide.filter((i) => i.code !== "other");
    const other = reasonsForHide.find((i) => i.code === "other");
    if (other) {
      setOtherReasonId(other.id);
    } else {
      setOtherReasonId(reasonsForHide.length);
    }
    return [
      ...withoutOther,
      other ?? {
        id: reasonsForHide.length,
        code: "other",
        title: "Other",
      },
    ];
  }, [reasonsForHide]);

  if (reasonsForHideList && !localHidden) {
    return (
      <RevPopoverMenu
        btnAs={RevButton}
        btnProps={{
          iconLeft: faEyeSlash,
          text: "Hide",
          variant: "secondary",
          buttonSize: "extraSmall",
        }}
        className="shadow-elevation--1 md:shadow-elevation-2 rounded overflow-hidden z-20"
        classNameBtn={`${width}`}
      >
        <div className={`bg-white w-full md:w-[364px] py-4 px-5.5`}>
          <div className="w-full flex md:hidden justify-between items-center mb-3">
            <h6>Select your reason for hiding</h6>
            <CloseButton className="hover:bg-monochrome-100 focus:bg-monochrome-200">
              <FontAwesomeIcon
                icon={faXmark}
                className={`text-monochrome-600 text-xl`}
              />
            </CloseButton>
          </div>
          <p className="typography-body-sm mb-2 hidden md:block">
            Select your reason for hiding
          </p>
          <div className="flex flex-wrap gap-2 mb-2">
            {reasonsForHideList.map((item: ReasonForHideType) => (
              <RevChip
                handleClick={() => setReason(item.id)}
                selected={selectedReasons.has(item.id)}
                text={item.title}
                key={`reason-for-hide-${item.id}-${creatorId}-${id}`}
              />
            ))}
          </div>
          {otherReasonId !== null && selectedReasons.has(otherReasonId) && (
            <RevTextArea
              name="reason"
              label="Other Reason"
              value={otherReason}
              onChange={(event) => setOtherReason(event.target.value)}
              useLength={false}
              placeholder="Enter other reason"
              rows={5}
            />
          )}
          {isSubmitting && (
            <p className="mt-1 typography-body-xs text-red-500 text-left">
              Please select or write a reason for hiding
            </p>
          )}
          <div className="flex justify-center gap-3 items-center mt-4">
            <CloseButton className="">
              <RevButton
                text="Cancel"
                variant="secondary"
                buttonSize="extraSmall"
                noButton
              />
            </CloseButton>
            <RevButton
              text="Submit"
              variant="primary"
              buttonSize="extraSmall"
              isLoading={isLoadingReasons}
              handleClick={() =>
                otherReason || selectedReasons.size > 0
                  ? handleHidden()
                  : setIsSubmitting(true)
              }
            />
          </div>
        </div>
      </RevPopoverMenu>
    );
  }
  if (localHidden) {
    return (
      <RevButton
        iconLeft={faEye}
        text="Unhide"
        variant="secondary"
        buttonSize="extraSmall"
        className="w-full"
        handleClick={() =>
          dispatch(deleteStatusCreator(creatorId)).then(() =>
            setLocalHidden(false),
          )
        }
      />
    );
  }

  return null;
};
